/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectorRef, Component, DestroyRef, ElementRef, ViewChild, OnInit, inject, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SharedModule } from '../../shared/shared.moudle';
import { MembersService } from '../members.service';
import { RegistrationLinkService, RegistrationLinkResolved } from '../registration-link.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ApiService } from '../../../services/api.service';
import { StorageService } from '../../../services/storage.service';
import { UploadService } from '../../../services/upload.service';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { SUCCESS, UserSuperData, LOCALE_INFO, LOCALIZATION } from '../../../data-models/user-data';
import { STATUS_ACTIVE, STATUS_PENDING_CONSENT, STATUS_PENDING_PASSWORD } from '../../../data-models/user-login';
import { environment } from '../../../../environments/environments';
import { startYearLessThanEndYear, startDateBeforeEndDate, noWhitespaceValidator, requireAtLeastOne, PHONE_PATTERN, syncCountryCodeRequiredWithPhone } from '../../../shared/forms/validators/form-validators';
import { VALIDATION_MESSAGES } from '../../../shared/forms/messages/validation-messages';
import { Observable, startWith, map, Subscription } from 'rxjs';
import { COUNTRY_CODES } from '../../../data-models/country-codes';
import { LoaderService } from '../../../services/loader.service';
import { RegistrationConfirmationComponent } from '../../shared/registration-confirmation/registration-confirmation.component';
import { PaymentService, PAYMENT_GATEWAY_STRIPE_ID, PAYMENT_GATEWAY_RAZORPAY_ID, INDIA_COUNTRY_ID } from '../../../services/payment.service';
import { RazorpayCheckoutService } from '../../../services/razorpay-checkout.service';

const storage_user = environment.storage_user;
const container_user = environment.container_user;

/** Subscription card view-model (mirrors membership-indi-v2's SubscriptionPlan). */
interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  duration: string;
  commitment: string;
  playersEnrolled: number;
  amount: number;
  discount: number;
  isActive: boolean;
  singularDuration?: string;
  isBasePlan?: boolean;  // Mark cards built from the plan's own amount/duration
}

/**
 * Personalized parent-invite registration wizard (public, no auth guard).
 *
 * Reached from the personalized link generated in the "Send Invitation Link" modal:
 *   /self-registration/invite?academy=..&parent=..&child=..&email=..&plan=..
 *
 * Steps: 0) intro/landing, 1) parent+player details (form copied from self-registration),
 *        2) subscription selection (subscriptions of the pre-selected plan), 3) payment.
 *
 * TODO(refactor): the step-1 form + its validators (checkUser/checkParentUser/checkAge/…) are
 * copied from `self-registration.component` for now. Once this flow is stable, extract the shared
 * player-info form into a reusable component/service so both flows use one source of truth.
 */
@Component({
  selector: 'app-self-registration-invite',
  standalone: true,
  imports: [SharedModule, RegistrationConfirmationComponent],
  templateUrl: './self-registration-invite.component.html',
  styleUrl: './self-registration-invite.component.scss'
})
export class SelfRegistrationInviteComponent implements OnInit, OnDestroy {
  @ViewChild('leftContainer') leftContainer: ElementRef | any;
  @ViewChild('card1') card1!: ElementRef;
  @ViewChild('card2') card2!: ElementRef;
  @ViewChild('card3') card3!: ElementRef;
  @ViewChild('card4') card4!: ElementRef;
  @ViewChild('card5') card5!: ElementRef;
  @ViewChild('card6') card6!: ElementRef;
  @ViewChild('card7') card7!: ElementRef;

  locale: LOCALE_INFO | any = {
    currency: '', date: '', locale: '', region: '', timezone: '', country: '', time: ''
  };

  // ---- wizard state -------------------------------------------------------
  current_step = 0;                 // 0 intro · 1 details · 2 subscription · 3 payment · 4 confirmation
  show_additional_info = false;
  loadingIntro = true;              // skeleton loader for step 0
  loadingForm = true;               // skeleton loader for step 1

  // Personalized-invite params (from the query string).
  academy_id: any = '';
  invite_parent = '';
  invite_child = '';
  invite_email = '';
  plan_id = '';
  /** Optional group id from the invite link (`?group=...`) — same param used by the group-scoped
   *  generic registration link (`self-registration/:id/:group`). Forwarded to the member-creation
   *  call so players who register via this link land in the right group, same as
   *  `SelfRegistrationComponent`. */
  group_id = '';

  /** Token from `/self-registration/invite/:token` — set only for the new token-based invite
   *  links (see resolveFromToken). Null for the legacy query-param links kept during the
   *  deprecation window. */
  inviteToken: string | null = null;
  /** True when the token doesn't resolve (not found / expired) — shown instead of the wizard. */
  inviteInvalid = false;
  inviteInvalidMessage = '';
  /** True when the resolved phase is password_set — registration is already fully done. */
  alreadyCompleted = false;

  // Landing (step 0) "we detected" editable state.
  edit_detected = false;
  detected_parent = '';
  detected_child = '';

  // Coach welcome video — placeholder. TODO: swap for the academy's real video URL.
  coachVideoUrl: SafeResourceUrl | any;

  academy_name = '';
  academy_dp: any = '';

  // ---- forms (copied structure from self-registration) --------------------
  addPersonalInfo!: FormGroup | any;
  addJerseyInfo: FormGroup | any;
  addPhysicalChara: FormGroup | any;
  addSportInfo: FormGroup | any;
  addAddressInfo: FormGroup | any;
  addEducation: FormGroup | any = new FormGroup({});
  addCareer: FormGroup | any;
  addAchievement: FormGroup | any;
  date: Date = new Date();
  submitted = false;
  isRegistering = false;

  validationMessages = VALIDATION_MESSAGES;

  email: any = '';
  already_a_academy_member = false;
  existingUser = false;
  existingUserID: any = '';
  parentExistingUser = false;
  parentExistingUserID: string | any;
  parent_already_a_academy_member = false;
  parent_is_academy = false;
  parentValid = true;
  isUnder13 = false;
  isValid: boolean | any;

  id: string | any;               // academy id used for product/state lookups
  countryName = 'Select country';
  countryList: any[] = [];
  countryListAll: any = [];
  searchTerm = '';
  countryId = '';
  countryIso = '';
  stateList: any = [];
  stateListAll: any = [];
  searchTermState = '';
  stateName = 'Select state';
  pincodeList: any = [];
  pincodeListAll: any = [];
  searchTermPincode = '';
  pincodeName = 'Select pincode';

  memberships: any[] = [];
  products: any = [];
  lookup = new Map();

  username: any = '';
  usernameValid = true;
  sportsList: any = [];
  educationList: any[] = [];
  careerList: any[] = [];
  achievementList: any[] = [];
  licenseList: any[] = [];
  country: any = '';
  state: any = '';
  postcode: any = '';
  generatedId = '';

  // Built player payload once step 1 passes validation.
  playerData: any = null;

  // ---- subscription (step 2) ---------------------------------------------
  subscriptionPlans: SubscriptionPlan[] = [];
  selectedSubscription: SubscriptionPlan | any = null;
  plan_name = '';
  loadingSubscriptions = false;
  hasSelectableSubscriptions = true;  // False if plan has zero associated_products (skip step 2)
  carouselIndex = 0;  // Current starting position in the carousel (shows cards [0-2], [3-5], etc.)
  expandedCards = new Set<string>();  // Track which cards (by id) are expanded to show all features

  // ---- payment (step 3) ---------------------------------------------------
  availablePaymentPortals: any[] = [];
  loadingPaymentPortals = false;
  selectedPortal: any = null;
  isProcessingPayment = false;
  createdMemberId: string | null = null;
  createdSubscriptionId: string | null = null;
  orderPreview: any = null;
  loadingOrderPreview = false;
  chargeAmount: number | null = null;
  academyPaymentOnboarded: boolean | null = null;  // null = loading, true = onboarded, false = not onboarded
  private registeredForSubscriptionId: string | null = null;
  private academyCountryId: string | null = null;
  private readonly PAYMENT_STATE_KEY = 'SELF_REG_PAYMENT_STATE';

  // ---- confirmation (step 4) ----------------------------------------------
  // Community-platform password-setup link (same link the backend emails the registrant).
  passwordSetupLink: string | null = null;
  passwordLinkLoading = false;
  passwordLinkError = false;
  private readonly destroyRef = inject(DestroyRef);

  phoneCodeControl = new FormControl('', [Validators.pattern(/^\+\d{1,4}$/)]);
  phoneCodeControlPlayer = new FormControl('', [Validators.pattern(/^\+\d{1,4}$/)]);
  filteredOptionsPhoneCodes: Observable<any[]> | undefined;
  filteredOptionsPhoneCodesPlayer: Observable<any[]> | undefined;
  phoneCode: any = COUNTRY_CODES;
  private subs: Subscription[] = [];

  modalReference: any;
  imageChangedEvent: any = '';
  file: File | any;
  fileSelect = true;
  afterImageUpload = false;
  fileSelectDone: boolean | any;
  croppedImage: any = '';
  afterCrop = false;

  constructor(
    private memberApi: MembersService,
    private registrationLinkApi: RegistrationLinkService,
    private api: ApiService,
    private storage: StorageService,
    private auth: AuthenticationService,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    public datepipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private uploadApi: UploadService,
    private router: Router,
    private loader: LoaderService,
    private sanitizer: DomSanitizer,
    private paymentService: PaymentService,
    private razorpayCheckout: RazorpayCheckoutService
  ) {}

  ngOnInit(): void {
    this.locale = this.storage.getJsonValue(LOCALIZATION);
    this.isValid = true;
    this.date.setFullYear(this.date.getFullYear() - 9);

    this.inviteToken = this.activatedRoute.snapshot.paramMap.get('token');

    if (this.inviteToken) {
      // Token-based invite (/self-registration/invite/:token) — the preload data and
      // onboarding phase live server-side now, not in the URL. initWizardChrome() runs the
      // parts of setup that don't need academy_id/plan_id yet; resolveFromToken fills those
      // in and jumps straight to whichever step this member has actually reached, instead of
      // always restarting at step 0.
      this.initWizardChrome();
      const paymentReturn = this.activatedRoute.snapshot.queryParamMap.get('payment_return');
      if (paymentReturn === 'cancel') {
        this.toastr.info('Payment was cancelled. You can choose a different payment method.', 'Cancelled', { progressBar: true, closeButton: true });
      }
      this.resolveFromToken(this.inviteToken);
      return;
    }

    // Legacy query-param link (kept for invites sent before token-based links shipped — see
    // resolveFromToken for the current flow). TODO(cleanup): remove this branch and the
    // `self-registration/invite` (no token) route once the deprecation window has passed.
    const qp = this.activatedRoute.snapshot.queryParamMap;
    this.academy_id = qp.get('academy') || '';
    this.invite_parent = qp.get('parent') || '';
    this.invite_child = qp.get('child') || '';
    this.invite_email = qp.get('email') || '';
    this.plan_id = qp.get('plan') || '';
    this.group_id = qp.get('group') || '';
    this.id = this.academy_id;
    this.detected_parent = this.invite_parent;
    this.detected_child = this.invite_child;

    // Returning from Stripe's hosted Checkout page (see payWithStripe()) — resume instead of
    // running the normal step-0 wizard init.
    const paymentReturn = qp.get('payment_return');
    if (paymentReturn === 'success' || paymentReturn === 'cancel') {
      this.getAcademyInfo();
      this.resumeAfterStripeRedirect(paymentReturn);
      return;
    }

    this.initWizardChrome();
    this.prefillFromInvite();
  }

  /**
   * Setup shared by both the token and legacy query-param paths that doesn't depend on
   * knowing academy_id/plan_id yet (those are set synchronously for the legacy path, but only
   * arrive asynchronously via resolveFromToken for the token path — loadProducts()/
   * getAcademyInfo() no-op without them and are re-run once resolveFromToken has them).
   */
  private initWizardChrome(): void {
    // Placeholder coach welcome video (autoplay, muted, loop). TODO: replace with real URL.
    this.coachVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1&mute=1&loop=1&playlist=dQw4w9WgXcQ&controls=1'
    );

    this.countryId = '';
    this.getCountryList();
    this.formsCollection();
    this.loadingForm = false;  // Form skeleton loading complete (forms initialized)
    this.loadProducts();
    this.getSportsList();
    this.generateUniqueId();
    this.getAcademyInfo();

    this.filteredOptionsPhoneCodes = this.phoneCodeControl.valueChanges.pipe(
      startWith(''), map(value => this._filterPhoneCodes(value || ''))
    );
    this.filteredOptionsPhoneCodesPlayer = this.phoneCodeControlPlayer.valueChanges.pipe(
      startWith(''), map(value => this._filterPhoneCodes(value || ''))
    );

    // Wire country code required validators: country code becomes required when its paired phone is filled
    this.subs.push(
      syncCountryCodeRequiredWithPhone(this.addPersonalInfo.controls['phonenumber'], this.phoneCodeControlPlayer)
    );
    this.subs.push(
      syncCountryCodeRequiredWithPhone(this.addPersonalInfo.controls['pphonenumber'], this.phoneCodeControl)
    );
  }

  /** Resolves a personalized-invite token against the backend and jumps to the member's
   *  actual onboarding phase instead of always restarting the wizard at step 0. */
  private resolveFromToken(token: string): void {
    this.registrationLinkApi.resolve(token).subscribe({
      next: (resolved) => this.applyResolvedInvite(resolved),
      error: (err) => {
        this.inviteInvalid = true;
        this.inviteInvalidMessage = err?.status === 410
          ? 'This invite link has expired. Please ask the academy to send you a new one.'
          : 'This invite link is no longer valid. Please ask the academy to send you a new one.';
        this.loadingIntro = false;
        this.loadingForm = false;
      }
    });
  }

  private applyResolvedInvite(resolved: RegistrationLinkResolved): void {
    this.academy_id = resolved.academy_id || '';
    this.id = this.academy_id;
    this.invite_parent = resolved.parent_name || '';
    this.invite_child = resolved.child_name || '';
    this.invite_email = resolved.email || '';
    this.plan_id = resolved.plan_id || '';
    this.group_id = resolved.group_id || '';
    this.detected_parent = this.invite_parent;
    this.detected_child = this.invite_child;

    // academy_id/plan_id weren't known when initWizardChrome() ran, so loadProducts() no-op'd
    // — retry it now. getAcademyInfo() is re-triggered per-branch below since the resume
    // branches need academyCountryId (for loadPaymentPortals) before they proceed, whereas
    // step 0 can render before it resolves, same as the legacy path's original timing.
    this.loadProducts();

    switch (resolved.phase) {
      case 'membership_assigned':
      case 'data_collected': // defensive — this flow always attaches a plan at insert time
        this.restoreForResume(resolved);
        this.getAcademyInfo(() => {
          this.current_step = 3;
          this.checkAcademyPaymentOnboarding();
          this.loadBillsBreakdown();
        });
        break;
      case 'fee_paid':
        // Already paid — never show the payment step again, go straight to password setup.
        this.restoreForResume(resolved);
        this.getAcademyInfo(() => this.goToConfirmation());
        break;
      case 'password_set':
        this.alreadyCompleted = true;
        break;
      default: // not_started / unknown
        this.current_step = 0;
        this.getAcademyInfo();
        this.prefillFromInvite();
    }
  }

  /** Restores the ids/plan cards the payment/confirmation steps need when resuming mid-flow
   *  (they'd otherwise only be set right after registerMemberAndSubscription() runs).
   *  Also populates subscriptionPlans/hasSelectableSubscriptions (via the same shared
   *  buildSubscriptionCards() helper loadSubscriptions() uses) so step 2 renders correctly
   *  if the user navigates back — not just step 3's summary — no matter which plan is
   *  currently assigned (e.g. after an admin edits it on the Sent Invites page). */
  private restoreForResume(resolved: RegistrationLinkResolved): void {
    this.createdMemberId = resolved.member_id || null;
    this.createdSubscriptionId = resolved.subscription_id || null;
    if (this.plan_id) {
      this.memberApi.getProductIndividual(this.plan_id).subscribe({
        next: (res: any) => {
          if (res.status === SUCCESS && res.data) {
            this.plan_name = res.data?.name || '';
            const { basePlanCard, associatedCards } = this.buildSubscriptionCards(res);
            this.hasSelectableSubscriptions = associatedCards.length > 0;
            this.subscriptionPlans = this.hasSelectableSubscriptions ? [basePlanCard, ...associatedCards] : [basePlanCard];
            // The live Subscription's product_id is always the base plan's id —
            // createSubscriptionForMember() always sends product_id: this.plan_id, never an
            // associated sub-plan's id — so the base plan card is definitionally what's
            // actually assigned/being paid for, not a guess.
            this.selectedSubscription = basePlanCard;
            this.registeredForSubscriptionId = basePlanCard.id;
          }
        },
        error: () => {
          this.toastr.error('Could not load your assigned plan. Please refresh and try again.', 'Error', { progressBar: true, closeButton: true });
        }
      });
    }
  }

  private _filterPhoneCodes(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.phoneCode.filter((option: any) => option.name.toLowerCase().includes(filterValue) || option.dial_code.toLowerCase().includes(filterValue));
  }

  // Split "First Last" into first/last for prefilling parent + child names.
  private splitName(full: string): { first: string; last: string } {
    const parts = (full || '').trim().split(/\s+/);
    return { first: parts.shift() || '', last: parts.join(' ') };
  }

  private prefillFromInvite(): void {
    const parent = this.splitName(this.detected_parent);
    const child = this.splitName(this.detected_child);
    this.addPersonalInfo.patchValue({
      fname: child.first,
      lname: child.last,
      pfname: parent.first,
      plname: parent.last,
      pemail: (this.invite_email || '').trim().toLowerCase(),
      prelation: 'Parent',
    });
    this.generateUsername();
    if (this.invite_email) {
      this.checkParentUser();
    }
  }

  /** @param onDone Optional — called once academyCountryId etc. are set, so a caller that
   *  needs them (e.g. loadPaymentPortals when resuming mid-flow) doesn't race this call. */
  getAcademyInfo(onDone?: () => void) {
    if (!this.academy_id) { onDone?.(); return; }
    this.auth.fetchUserData(this.academy_id).subscribe({
      next: (user) => {
        this.academy_name = user.data.basic_information.name.first_name.concat(' ').concat(user.data.basic_information.name.last_name.toString());
        this.academy_dp = user.data.basic_information.dp;
        this.academyCountryId = user.data.country?.[0] || null;
        // This is a public (logged-out) page, so storage has no LOCALIZATION and currencyLocale would
        // fall back to USD. Use the academy's own localization so amounts render in its currency.
        if (user.data.localization) {
          this.locale = user.data.localization;
          this.auth.storeLocale(user.data.localization);
        }
        this.loadingIntro = false;  // Intro skeleton loading complete
        onDone?.();
      },
      // Must still call onDone on error — otherwise a resumed session (whose step-jump is
      // driven entirely by this callback, see applyResolvedInvite) would silently strand the
      // parent at step 0 forever if this fetch ever fails, even though the phase itself
      // resolved correctly.
      error: () => {
        this.loadingIntro = false;
        onDone?.();
      }
    });
  }

  // ---- step 0 (intro) helpers --------------------------------------------
  toggleEditDetected() {
    this.edit_detected = !this.edit_detected;
  }

  updateDetected() {
    this.invite_parent = this.detected_parent;
    this.invite_child = this.detected_child;
    this.prefillFromInvite();
    this.edit_detected = false;
    this.toastr.success('Details updated.', 'Updated', { progressBar: true, closeButton: true });
  }

  startRegistration() {
    this.current_step = 1;
  }

  // ---- forms --------------------------------------------------------------
  formsCollection() {
    this.addPersonalInfo = new FormGroup({
      email: new FormControl('', [Validators.email]),
      dob: new FormControl(this.date, [Validators.required]),
      fname: new FormControl('', [Validators.required, noWhitespaceValidator()]),
      lname: new FormControl(''),
      username: new FormControl('', [Validators.required]),
      gender: new FormControl('', [Validators.required]),
      plan: new FormControl(''),
      pemail: new FormControl('', [Validators.email]), // required-ness is handled by the group-level email-OR-phone rule
      pfname: new FormControl('', [Validators.required, noWhitespaceValidator()]),
      plname: new FormControl(''),
      pphonenumber: new FormControl('', [Validators.pattern('^\\+?[1-9]\\d{1,14}$')]),
      phonenumber: new FormControl('', [Validators.pattern('^\\+?[1-9]\\d{1,14}$')]),
      prelation: new FormControl('Parent'),
      reg_number: new FormControl(''),
      reg_authority: new FormControl(''),
      blood_group: new FormControl(''),
    });
    this.addPersonalInfo.patchValue({ lname: '', plan: '', prelation: 'Parent' });
    this.checkAge();

    this.addJerseyInfo = new FormGroup({
      t_shirt_size: new FormControl(''),
      shorts_size: new FormControl(''),
      shoe_size: new FormControl('', [Validators.pattern(/^\d*\.?\d+$/)])
    });

    this.addPhysicalChara = new FormGroup({
      height: new FormControl('', [Validators.pattern(/^\d*\.?\d+$/)]),
      weight: new FormControl('', [Validators.pattern(/^\d*\.?\d+$/)]),
      bmi: new FormControl('', [Validators.pattern(/^\d*\.?\d+$/)])
    });
    this.addPhysicalChara.get('height').valueChanges.subscribe(() => this.calculateBMI());
    this.addPhysicalChara.get('weight').valueChanges.subscribe(() => this.calculateBMI());

    this.addSportInfo = new FormGroup({
      sport_name: new FormControl(''),
      position: new FormControl('')
    });

    this.addAddressInfo = new FormGroup({
      address: new FormControl(''),
      city: new FormControl(''),
    });

    this.addEducation = new FormGroup({
      institute: new FormControl('', [noWhitespaceValidator()]),
      discipline: new FormControl('', [noWhitespaceValidator()]),
      start_Year: new FormControl('', [Validators.pattern(/^(19|20)\d{2}$/), noWhitespaceValidator()]),
      end_Year: new FormControl('', [Validators.pattern(/^(19|20)\d{2}$/), noWhitespaceValidator()])
    }, { validators: startYearLessThanEndYear() });
    this.addEducation.statusChanges.subscribe(() => this.checkFormValidity());

    this.addCareer = new FormGroup({
      academy: new FormControl('', [noWhitespaceValidator()]),
      role: new FormControl('', [noWhitespaceValidator()]),
      start_date: new FormControl(''),
      end_date: new FormControl('')
    }, { validators: startDateBeforeEndDate() });
    this.addCareer.statusChanges.subscribe(() => this.checkCareerValidity());

    this.addAchievement = new FormGroup({
      event: new FormControl('', [noWhitespaceValidator()]),
      achievement: new FormControl('', [noWhitespaceValidator()]),
      year: new FormControl('', [noWhitespaceValidator(), Validators.pattern(/^(19|20)\d{2}$/)])
    });
    this.licenseList = [];
    this.addAchievement.statusChanges.subscribe(() => this.checkAchievementValidity());
  }

  generateUniqueId() {
    this.generatedId = '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Step-1 "Next Step": validate the details form, build the single-player payload and advance to
   * the subscription step.
   */
  goToSubscriptions() {
    this.submitted = true;
    this.checkAge();
    if (this.phoneCodeControl.invalid || this.phoneCodeControlPlayer.invalid) {
      this.toastr.warning('Please check the form for missing or incorrect details.', 'Invalid data', { progressBar: true, closeButton: true });
      return;
    }
    if (!(this.addPersonalInfo.valid && this.isValid && this.parentValid && !this.usernameValid)) {
      this.toastr.warning('Please check the form for missing or incorrect details.', 'Invalid data', { progressBar: true, closeButton: true });
      return;
    }
    const email = this.addPersonalInfo.get('email').value.trim().toLowerCase();
    if (email && email === this.addPersonalInfo.get('pemail')?.value.toLowerCase().trim()) {
      this.toastr.warning('Student and parent email cannot be the same.', 'Invalid data', { progressBar: true, closeButton: true });
      return;
    }

    this.playerData = this.buildPlayerPayload();
    this.current_step = 2;
    this.loadSubscriptions();
  }

  private buildPlayerPayload() {
    const dobValue = this.addPersonalInfo.get('dob').value;
    const dob = new Date(dobValue);
    const formattedDob = `${dob.getDate().toString().padStart(2, '0')} ${(dob.getMonth() + 1).toString().padStart(2, '0')} ${dob.getFullYear()}`;
    const tempPassword = `${this.addPersonalInfo.get('fname').value.trim().toUpperCase()}${this.datepipe.transform(this.date, 'ddMMYYYY')}P@ssw0rd`;
    const salt = this.storage.getSalt();
    const pass = this.auth.generatePasswordHash(salt, tempPassword);
    const newSalt = this.auth.convertByteToSalt(salt);

    return {
      self_registration: true,
      address: this.addAddressInfo.get('address').value.trim(),
      city: this.addAddressInfo.get('city').value.trim(),
      country: this.countryId,
      dob: formattedDob,
      email: this.addPersonalInfo.get('email').value.toLowerCase().trim(),
      first_name: this.addPersonalInfo.get('fname').value.trim(),
      gender: this.addPersonalInfo.get('gender').value,
      last_name: this.addPersonalInfo.get('lname').value.trim(),
      pemail: this.addPersonalInfo.get('pemail').value.toLowerCase().trim(),
      pfirst_name: this.addPersonalInfo.get('pfname').value.trim(),
      phone: this.addPersonalInfo.get('pphonenumber').value.trim(),
      phone_code_p: this.phoneCodeControl.value,
      user_phone: this.addPersonalInfo.get('phonenumber')?.value.trim(),
      phone_code_player: this.phoneCodeControlPlayer.value,
      pincode: this.postcode,
      plast_name: this.addPersonalInfo.get('plname').value.trim(),
      relation: this.addPersonalInfo.get('prelation').value.trim(),
      profile: 'Player',
      state: this.state,
      underage: this.isUnder13,
      user_name: this.username,
      unique_id: this.generatedId,
      product_role: this.plan_id,
      password: pass,
      tempPassword: tempPassword,
      salt: newSalt,
      reg_number: this.addPersonalInfo.get('reg_number').value.trim(),
      reg_authority: this.addPersonalInfo.get('reg_authority').value,
      blood_group: this.addPersonalInfo.get('blood_group').value,
      jersey_sizes: {
        t_shirt: this.addJerseyInfo.controls['t_shirt_size'].value,
        shorts: this.addJerseyInfo.controls['shorts_size'].value,
        shoe: this.addJerseyInfo.controls['shoe_size'].value.toString() || ''
      },
      physical_charateristics: {
        height: +this.addPhysicalChara.get('height').value || 0,
        weight: +this.addPhysicalChara.get('weight').value || 0,
      },
      sport_info: {
        sports_id: this.addSportInfo.get('sport_name').value,
        prefered_position: this.addSportInfo.get('position').value
      },
      education: this.educationList,
      careers: this.careerList,
      achievements: this.achievementList,
      licenses: this.licenseList,
    };
  }

  cancelRegistration() {
    this.submitted = false;
    if (this.inviteToken) {
      // Token path: there's no query string to fall back to, so re-resolve and land on
      // whatever phase this member is actually in, rather than hardcoding step 0.
      this.resolveFromToken(this.inviteToken);
      return;
    }
    // Legacy query-param path — no server state to re-resolve against, just reset locally.
    this.current_step = 0;
    this.playerData = null;
    this.selectedSubscription = null;
    this.createdMemberId = null;
    this.createdSubscriptionId = null;
    this.registeredForSubscriptionId = null;
    this.selectedPortal = null;
    this.storage.setJsonValue(this.PAYMENT_STATE_KEY, null);
  }

  // ---- step 2 (subscriptions) --------------------------------------------
  /** Helper to turn credits + duration_type into a commitment string (e.g. "3 months commitment"). */
  private toCommitment(credits: number, durationType: string): string {
    const rawDurationType = durationType || 'months';
    let singularDuration = rawDurationType;
    if (credits === 1 && typeof rawDurationType === 'string' && ['days', 'months', 'years'].includes(rawDurationType.toLowerCase())) {
      singularDuration = rawDurationType.slice(0, -1);
    }
    return `${credits} ${singularDuration} commitment`;
  }

  /** Map a product object (plan or associated_product) to a SubscriptionPlan card. */
  private toSubscriptionCard(product: any, isBasePlan = false, sliceName = false): SubscriptionPlan {
    const credits = product.credits ?? 0;
    const durationType = product.duration_type || 'months';
    return {
      id: product._id,
      name: sliceName ? (product.name.split(':')[1] ?? product.name) : product.name,
      description: product.description || '',
      duration: product.duration || '',
      commitment: this.toCommitment(credits, durationType),
      playersEnrolled: product.total_player || 0,
      amount: product.amount || 0,
      discount: product.discount || 0,
      isActive: !product.is_Paused,
      singularDuration: durationType,
      isBasePlan
    } as SubscriptionPlan;
  }

  /** Shared plan/associated-products → card mapping, used by both the fresh step-2 flow
   *  (loadSubscriptions) and the resume path (restoreForResume) so both populate
   *  subscriptionPlans/hasSelectableSubscriptions identically — previously only
   *  loadSubscriptions() did this, so resuming an invite left step 2 permanently empty even
   *  after an admin changed the assigned plan to one with real selectable sub-plans. */
  private buildSubscriptionCards(res: any): { basePlanCard: SubscriptionPlan; associatedCards: SubscriptionPlan[] } {
    // Build the base plan card (the plan's own amount/duration).
    const basePlanCard = this.toSubscriptionCard(res.data, true, false);
    // Build subscription cards from associated_products, slicing their names on `:` convention.
    const associatedCards = (res.associated_products || [])
      .map((sub: any) => this.toSubscriptionCard(sub, false, true))
      .filter((s: SubscriptionPlan) => s.isActive);
    return { basePlanCard, associatedCards };
  }

  /** Reuses the membership-indi-v2 `associated_products` → subscriptions mapping. */
  loadSubscriptions() {
    if (!this.plan_id) return;
    this.loadingSubscriptions = true;
    this.memberApi.getProductIndividual(this.plan_id).subscribe({
      next: (res: any) => {
        if (res.status === SUCCESS) {
          this.plan_name = res.data?.name || '';
          const { basePlanCard, associatedCards } = this.buildSubscriptionCards(res);
          // If no associated products, skip step 2 and auto-select the base plan.
          this.hasSelectableSubscriptions = associatedCards.length > 0;
          if (!this.hasSelectableSubscriptions) {
            this.subscriptionPlans = [basePlanCard];
            this.selectedSubscription = basePlanCard;
            this.enterPaymentStep();  // Skip step 2, register + go straight to payment
          } else {
            // Show base plan + associated products (if any).
            this.subscriptionPlans = [basePlanCard, ...associatedCards];
            // Auto-select the first (base) plan initially
            this.selectedSubscription = basePlanCard;
            this.current_step = 2;
          }
        } else {
          this.toastr.error('It looks like the server is busy now, try again later.', 'Server down');
        }
      },
      error: () => { this.loadingSubscriptions = false; },
      complete: () => { this.loadingSubscriptions = false; }
    });
  }

  selectSubscription(sub: SubscriptionPlan) {
    this.selectedSubscription = sub;
  }

  /** Move carousel to next 3 cards. */
  carouselNext() {
    const nextIndex = this.carouselIndex + 3;
    if (nextIndex < this.subscriptionPlans.length) {
      this.carouselIndex = nextIndex;
    }
  }

  /** Move carousel to previous 3 cards. */
  carouselPrev() {
    const prevIndex = this.carouselIndex - 3;
    if (prevIndex >= 0) {
      this.carouselIndex = prevIndex;
    }
  }

  /** Get the 3 visible cards for the current carousel position. */
  visibleCards(): SubscriptionPlan[] {
    return this.subscriptionPlans.slice(this.carouselIndex, this.carouselIndex + 3);
  }

  /** Check if there are more cards to the right. */
  canCarouselNext(): boolean {
    return this.carouselIndex + 3 < this.subscriptionPlans.length;
  }

  /** Check if there are more cards to the left. */
  canCarouselPrev(): boolean {
    return this.carouselIndex > 0;
  }

  /** Toggle expand/collapse for a card's feature list. */
  toggleCardExpand(cardId: string) {
    if (this.expandedCards.has(cardId)) {
      this.expandedCards.delete(cardId);
    } else {
      this.expandedCards.add(cardId);
    }
  }

  /** Check if a card is expanded. */
  isCardExpanded(cardId: string): boolean {
    return this.expandedCards.has(cardId);
  }

  /** Get visible features (first 4 or all if expanded). */
  visibleFeatures(sub: SubscriptionPlan): string[] {
    const allFeatures = this.subFeatures(sub);
    if (this.isCardExpanded(sub.id) || allFeatures.length <= 3) {
      return allFeatures;
    }
    return allFeatures.slice(0, 3);
  }

  /** Check if card has more than 3 features. */
  hasMoreFeatures(sub: SubscriptionPlan): boolean {
    return this.subFeatures(sub).length > 3;
  }

  /**
   * Turn a subscription description into feature bullets (like the reference plan cards).
   * Splits on newlines/bullets/periods/colons to create discrete feature items.
   * Returns [] for empty/placeholder values so the card just shows name + price + commitment.
   */
  subFeatures(sub: SubscriptionPlan): string[] {
    const desc = (sub?.description || '').trim();
    if (!desc || desc.toLowerCase() === 'n/a' || desc.toLowerCase() === 'nil') return [];

    let parts: string[] = [];

    // First try splitting on explicit delimiters (newlines, bullets, dashes)
    parts = desc.split(/\r?\n|•|-(?=\s)/);

    // If still only one part, try splitting on periods
    if (parts.length === 1) {
      parts = desc.split(/(?<=\.)\s+/);
    }

    // If still only one part and it's long, try splitting on colons (feature: description)
    if (parts.length === 1 && desc.length > 80) {
      parts = desc.split(/(?<=:)\s+/);
    }

    // Clean and filter parts: remove prefixes, trim, filter short/empty strings
    return parts
      .map(p => p.trim().replace(/^[-•.\s*]+/, ''))
      .filter(p => p.length > 3 && !p.match(/^\d+\s*$|^n\/a$|^nil$/i));
  }

  continueToPayment() {
    // Auto-select the first (basic) plan if none is selected
    if (!this.selectedSubscription && this.subscriptionPlans?.length > 0) {
      this.selectedSubscription = this.subscriptionPlans[0];
    }
    if (!this.selectedSubscription) {
      this.toastr.warning('Please select a subscription to continue.', 'Select a plan', { progressBar: true, closeButton: true });
      return;
    }
    this.enterPaymentStep();
  }

  /**
   * Registers the member + subscription (if not already done for the currently selected
   * subscription) and advances to step 3. Re-entrant: navigating back to step 2 and forward
   * again without changing the plan won't re-register.
   */
  private enterPaymentStep(): void {
    if (this.createdSubscriptionId && this.registeredForSubscriptionId === this.selectedSubscription?.id) {
      this.current_step = 3;
      this.checkAcademyPaymentOnboarding();
      return;
    }
    this.registerMemberAndSubscription();
  }

  private registerMemberAndSubscription(): void {
    this.loader.show();
    // addMemberSelf (/members/:id/add-parent) 401s for guests — it has no other caller in this
    // codebase to prove it's actually open to unauthenticated requests. addMemberMultipleMembers
    // (/members-v2/:id/add) is the endpoint the sibling public self-registration page already uses
    // successfully as a guest, so we reuse that proven-working path here (single-item array — this
    // invite flow only ever registers one child).
    this.memberApi.addMemberMultipleMembers([this.playerData], this.academy_id, this.group_id, this.inviteToken || undefined).subscribe({
      next: (res: any) => {
        const entry = res?.[0];
        // Confirmed real shape: { data: "<new member id>", response: "success" } — `data` is the
        // id itself (a string), not a nested object. Kept the object-shaped fallbacks below in case
        // an error/partial-failure entry ever comes back shaped differently.
        const newMemberId = (typeof entry?.data === 'string' ? entry.data : undefined)
          || entry?._id || entry?.id || entry?.user_id || entry?.data?._id || entry?.data?.id;
        if (entry?.response !== SUCCESS || !newMemberId) {
          this.loader.hide();
          // Response shape here has no prior precedent (no existing caller reads an id off it) —
          // if this fires, inspect the logged payload and adjust the field names tried above.
          console.error('Unexpected addMemberMultipleMembers response shape:', res);
          this.toastr.error('Could not create the registration. Please try again.', 'Registration failed', { progressBar: true, closeButton: true });
          return;
        }
        this.createdMemberId = newMemberId;
        this.createSubscriptionForMember();
      },
      error: () => {
        this.loader.hide();
        this.toastr.error('Could not create the registration. Please try again.', 'Registration failed', { progressBar: true, closeButton: true });
      }
    });
  }

  private createSubscriptionForMember(): void {
    const payload = {
      product_id: this.plan_id,
      // Staff-driven callers of createSubscriptions rely on the backend inferring academy_id from
      // the authenticated session; this is a guest call, so it must be sent explicitly.
      academy_id: this.academy_id,
      user_id: this.createdMemberId,
      discount_code: '',
      status: 'active',
      discount_amount: `${this.selectedSubscription?.amount}`,
      grace: 10,
      start_date: new Date(),
    };
    this.memberApi.createSubscriptions(payload).subscribe({
      next: (res: any) => {
        this.loader.hide();
        // The subscription ID must come from the backend response — DO NOT fall back to the
        // product ID. The payment webhook looks up the subscription by this ID; if we send a
        // product ID instead, the webhook will fail to find the subscription and leave the
        // payment orphaned forever (MONGO: no documents in result).
        // Backend returns raw mongo.InsertOneResult marshaled with no json tag, so the field
        // name is Go's default (PascalCase) — confirmed live: { InsertedID: "<ObjectID>" }.
        // Backend engineering claimed snake_case (inserted_id) but that did not match what was
        // actually observed on the wire — check both in case the backend's json tag changes later.
        const subscriptionId = res?.InsertedID || res?.inserted_id;
        if (!subscriptionId) {
          console.error('createSubscriptions response did not include subscription ID:', res);
          this.toastr.error('Your registration was created but the subscription ID could not be read. Please contact the academy.', 'Subscription setup incomplete', { progressBar: true, closeButton: true });
          return;
        }
        this.createdSubscriptionId = subscriptionId;
        this.registeredForSubscriptionId = this.selectedSubscription?.id || null;
        this.current_step = 3;
        this.checkAcademyPaymentOnboarding();
        this.loadBillsBreakdown();
      },
      error: () => {
        this.loader.hide();
        this.toastr.error('Your registration was created but the subscription could not be set up. Please contact the academy.', 'Subscription failed', { progressBar: true, closeButton: true });
      }
    });
  }

  loadPaymentPortals(): void {
    this.loadingPaymentPortals = true;
    this.selectedPortal = null;
    this.paymentService.getLocalePaymentOptions(this.academyCountryId || undefined).subscribe({
      next: (res: any) => {
        this.loadingPaymentPortals = false;
        if (res?.status === SUCCESS && Array.isArray(res.data)) {
          this.availablePaymentPortals = res.data;
          if (res.data.length === 1) {
            this.selectedPortal = res.data[0];
          } else if (!res.data.length) {
            this.toastr.warning('No payment methods are available for this academy yet. Please contact them directly.', 'No payment methods', { progressBar: true, closeButton: true });
          }
        } else {
          this.availablePaymentPortals = [];
        }
      },
      error: () => {
        this.loadingPaymentPortals = false;
        this.availablePaymentPortals = [];
      }
    });
  }

  private checkAcademyPaymentOnboarding(): void {
    this.academyPaymentOnboarded = null;  // reset to loading state
    this.registrationLinkApi.checkPaymentOnboardingStatus(this.academy_id).subscribe({
      next: (res: any) => {
        this.academyPaymentOnboarded = res?.is_onboarded === true;
        if (this.academyPaymentOnboarded) {
          this.loadPaymentPortals();
        }
      },
      error: (err) => {
        // Fail open: treat as onboarded on any error (transient network issue, etc.)
        // so the registration flow isn't blocked. The existing fallback toasts
        // (handlePaymentOrderError, "payment setup is incomplete") remain as safety nets.
        console.error('Failed to check academy payment onboarding status:', err);
        this.academyPaymentOnboarded = true;
        this.loadPaymentPortals();
      }
    });
  }

  backToSubscriptions() {
    // If the plan had no selectable subscriptions (step 2 was skipped), go back to step 1 (details).
    // Otherwise, go back to step 2 (subscription selection).
    this.current_step = this.hasSelectableSubscriptions ? 2 : 1;
  }

  get isIndiaAcademy(): boolean {
    return this.academyCountryId === INDIA_COUNTRY_ID;
  }

  get displayTotal(): number {
    return (this.isIndiaAcademy && this.chargeAmount) ? this.chargeAmount : (this.selectedSubscription?.amount || 0);
  }

  // ---- step 3 (payment) ---------------------------------------------------
  private loadBillsBreakdown(): void {
    if (!this.isIndiaAcademy) { return; }
    this.loadingOrderPreview = true;
    // fetchDue generates billing_cycles if needed and returns chargesBreakdown directly in the response.
    this.memberApi.fetchDue(this.createdMemberId!, this.academy_id).subscribe({
      next: (res: any) => {
        const charges = res?.data?.chargesBreakdown;
        this.orderPreview = charges ? { ...charges, discount: res?.data?.total_discount || 0 } : null;
        this.chargeAmount = res?.data?.finalAmount || null;
        this.loadingOrderPreview = false;
      },
      error: () => {
        this.orderPreview = null;
        this.chargeAmount = null;
        this.loadingOrderPreview = false;
      }
    });
  }

  selectPaymentMethod(portal: any) {
    this.selectedPortal = portal;
  }

  confirmAndPay() {
    if (!this.selectedPortal) {
      this.toastr.warning('Please select a payment method.', 'Payment method required', { progressBar: true, closeButton: true });
      return;
    }
    if (!this.createdSubscriptionId) {
      this.toastr.error('Something went wrong setting up your registration. Please go back and try again.', 'Registration error', { progressBar: true, closeButton: true });
      return;
    }

    // The admin may have changed the assigned plan after this browser loaded it — re-check
    // against the live invite before ever reaching the gateway, so a stale amount/plan is
    // never charged. Only meaningful for token-based invites (nothing to re-resolve for the
    // legacy query-param path).
    if (this.inviteToken) {
      this.verifyPlanIsCurrentThenPay();
      return;
    }
    this.dispatchToGateway();
  }

  /** Re-resolves the invite token and compares its plan against what's currently loaded
   *  before letting the parent proceed to payment. If the plan has changed, refreshes the
   *  wizard's state instead of charging against stale data. */
  private verifyPlanIsCurrentThenPay(): void {
    this.isProcessingPayment = true;
    this.registrationLinkApi.resolve(this.inviteToken!).subscribe({
      next: (resolved) => {
        this.isProcessingPayment = false;
        if (resolved.plan_id && resolved.plan_id !== this.plan_id) {
          this.toastr.warning(
            'The membership plan for this registration was updated. Please review the new details before paying.',
            'Plan Updated',
            { progressBar: true, closeButton: true }
          );
          this.applyResolvedInvite(resolved);
          return;
        }
        this.dispatchToGateway();
      },
      error: () => {
        this.isProcessingPayment = false;
        this.toastr.error('Could not verify your registration details. Please try again.', 'Error', { progressBar: true, closeButton: true });
      }
    });
  }

  private dispatchToGateway(): void {
    const gatewayId = this.selectedPortal?.payment_info?._id || this.selectedPortal?.payment;
    switch (gatewayId) {
      case PAYMENT_GATEWAY_RAZORPAY_ID:
        this.payWithRazorpay();
        break;
      case PAYMENT_GATEWAY_STRIPE_ID:
        this.payWithStripe();
        break;
      default:
        this.toastr.error('This payment method is not supported yet.', 'Unsupported payment method', { progressBar: true, closeButton: true });
    }
  }

  private buildPaymentOrderPayload(): any {
    const amount = this.displayTotal;
    return {
      amount: Math.round(amount * 100),
      currency: this.locale?.currency || 'USD',
      note: this.academy_id,
      type: this.selectedPortal?.type || 'single',
      fee: this.selectedPortal?.payment_info?.detail?.fee || 2,
      // The backend's payment webhook resolves the self-registered member from this field
      // (notes.customer_info) to auto-activate them out of "pending verification" once payment
      // is confirmed — it must be the member's id, not a display name.
      customer: this.createdMemberId,
      intent: {
        subscription_id: this.createdSubscriptionId,
        status: 'success',
        txn_type: 'online',
        amount,
        is_partial: false,
        pending_due: 0,
        paid_cycles: 1,
        cycles_covered: 1,
        product: this.plan_id,
      },
    };
  }

  /**
   * Shared handling for payment-order-creation failures (Razorpay/Stripe). If the backend
   * rejects because the registration was already paid elsewhere in the meantime (see
   * rejectSelfRegistrationDoublePay), re-resolve the token to refresh state instead of
   * leaving the user to retry into the exact same rejection.
   */
  private handlePaymentOrderError(err: any): void {
    this.isProcessingPayment = false;
    if (err?.error?.code === 'ALREADY_PAID') {
      this.toastr.warning(
        err?.error?.message || 'This registration has already been paid for.',
        'Already Paid',
        { progressBar: true, closeButton: true }
      );
      if (this.inviteToken) {
        this.resolveFromToken(this.inviteToken);
      }
      return;
    }
    this.toastr.error('Could not start the payment. Please try again.', 'Payment error', { progressBar: true, closeButton: true });
  }

  private payWithRazorpay(): void {
    this.isProcessingPayment = true;
    const payload = this.buildPaymentOrderPayload();
    this.paymentService.createOrder(payload).subscribe({
      next: (res: any) => {
        const options = {
          key: environment.razorpay_key,
          amount: res.amount,
          currency: res.currency,
          name: 'Dartle',
          description: `Registration: ${this.selectedSubscription?.name || ''}`,
          order_id: res.id,
          prefill: { name: this.invite_parent || '', email: this.invite_email || '' },
          theme: { color: '#ff6634' },
        };
        this.razorpayCheckout.open(options).then((response: any) => {
          const data = {
            subscription_id: this.createdSubscriptionId,
            status: 'success',
            txn_type: 'online',
            amount: payload.intent.amount,
            extra: response,
          };
          // The gateway webhook is the source of truth; this client-side finalize call is a
          // best-effort fast path, so we advance either way.
          this.paymentService.createTransactions(data).subscribe({
            next: () => { this.isProcessingPayment = false; this.goToConfirmation(); },
            error: () => { this.isProcessingPayment = false; this.goToConfirmation(); }
          });
        }).catch(() => {
          this.isProcessingPayment = false;
          this.toastr.info('Payment was not completed.', 'Payment cancelled', { progressBar: true, closeButton: true });
        });
      },
      error: (err) => this.handlePaymentOrderError(err)
    });
  }

  private payWithStripe(): void {
    this.isProcessingPayment = true;
    const payload = this.buildPaymentOrderPayload();
    const returnUrl = this.buildInviteReturnUrl();
    const sep = returnUrl.includes('?') ? '&' : '?';
    (payload as any).success_url = `${returnUrl}${sep}payment_return=success`;
    (payload as any).cancel_url = `${returnUrl}${sep}payment_return=cancel`;

    this.paymentService.createOrderStripe(payload).subscribe({
      next: (res: any) => {
        if (!res?.url) {
          this.isProcessingPayment = false;
          this.toastr.error("This academy's payment setup is incomplete. Please contact them directly.", 'Payment error', { progressBar: true, closeButton: true });
          return;
        }
        this.storage.setJsonValue(this.PAYMENT_STATE_KEY, {
          sessionId: res.session,
          createdMemberId: this.createdMemberId,
          createdSubscriptionId: this.createdSubscriptionId,
          selectedSubscription: this.selectedSubscription,
          academyCountryId: this.academyCountryId,
        });
        window.location.href = res.url;
      },
      error: (err) => this.handlePaymentOrderError(err)
    });
  }

  private buildInviteReturnUrl(): string {
    if (this.inviteToken) {
      // Token path: the return URL just needs the token — resolveFromToken re-derives
      // everything else (including the phase) fresh on return, so there's no preload data to
      // re-serialize into the query string.
      return `${window.location.origin}/self-registration/invite/${this.inviteToken}`;
    }
    const params = new URLSearchParams();
    if (this.academy_id) params.set('academy', this.academy_id);
    if (this.invite_parent) params.set('parent', this.invite_parent);
    if (this.invite_child) params.set('child', this.invite_child);
    if (this.invite_email) params.set('email', this.invite_email);
    if (this.plan_id) params.set('plan', this.plan_id);
    return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
  }

  /** Resumes the wizard after redirecting to Stripe's hosted Checkout and back. */
  private resumeAfterStripeRedirect(result: 'success' | 'cancel'): void {
    const state = this.storage.getJsonValue(this.PAYMENT_STATE_KEY);

    if (result === 'cancel') {
      this.toastr.info('Payment was cancelled. You can choose a different payment method.', 'Cancelled', { progressBar: true, closeButton: true });
      if (state?.createdSubscriptionId) {
        this.createdMemberId = state.createdMemberId;
        this.createdSubscriptionId = state.createdSubscriptionId;
        this.selectedSubscription = state.selectedSubscription;
        this.registeredForSubscriptionId = state.selectedSubscription?.id || null;
        this.academyCountryId = state.academyCountryId;
        this.current_step = 3;
        this.loadPaymentPortals();
      } else {
        this.current_step = 0;
      }
      return;
    }

    // success
    if (!state?.sessionId) {
      this.current_step = 0;
      return;
    }
    // Restore the created member so the confirmation step can fetch its password-setup link
    // (the stored state is cleared once the transaction is confirmed).
    this.createdMemberId = state.createdMemberId || null;
    this.createdSubscriptionId = state.createdSubscriptionId || null;
    this.current_step = 3;
    this.isProcessingPayment = true;
    this.pollTransactionStatus(state.sessionId, 0);
  }

  private pollTransactionStatus(sessionId: string, attempt: number): void {
    const MAX_ATTEMPTS = 10;
    const POLL_INTERVAL_MS = 2000;
    const onSettled = () => {
      if (attempt + 1 >= MAX_ATTEMPTS) {
        this.isProcessingPayment = false;
        this.storage.setJsonValue(this.PAYMENT_STATE_KEY, null);
        this.toastr.info("Payment received — we're confirming it now. You'll get a confirmation email shortly.", 'Payment received', { progressBar: true, closeButton: true });
        this.goToConfirmation();
        return;
      }
      setTimeout(() => this.pollTransactionStatus(sessionId, attempt + 1), POLL_INTERVAL_MS);
    };

    this.paymentService.fetchTransactionStatus(sessionId).subscribe({
      next: (res: any) => {
        if (res?.status === SUCCESS) {
          this.isProcessingPayment = false;
          this.storage.setJsonValue(this.PAYMENT_STATE_KEY, null);
          this.goToConfirmation();
          return;
        }
        onSettled();
      },
      error: () => onSettled()
    });
  }

  // ---- step 4 (confirmation) ----------------------------------------------
  /** Advances to the confirmation step and loads the registrant's password-setup link. */
  private goToConfirmation(): void {
    this.current_step = 4;
    this.fetchPasswordSetupLink();
  }

  /**
   * Fetches the password-setup link (same link as the invite email) for the newly created
   * member from the PUBLIC self-registration endpoint. The backend only issues it once the
   * payment webhook has accepted the member, answering `pending` until then — so we poll a
   * few times, mirroring pollTransactionStatus. Any final failure falls back to the
   * "check your email" note on the confirmation screen; it never affects auth state.
   */
  private fetchPasswordSetupLink(attempt = 0): void {
    const MAX_ATTEMPTS = 10;
    const POLL_INTERVAL_MS = 2000;

    if (attempt === 0) {
      this.passwordSetupLink = null;
      this.passwordLinkError = false;
    }
    if (!this.academy_id || !this.createdMemberId) {
      this.passwordLinkError = true;
      return;
    }
    this.passwordLinkLoading = true;

    const fail = () => { this.passwordLinkError = true; this.passwordLinkLoading = false; };
    const retryOrFail = () => {
      if (attempt + 1 >= MAX_ATTEMPTS) { fail(); return; }
      setTimeout(() => this.fetchPasswordSetupLink(attempt + 1), POLL_INTERVAL_MS);
    };

    this.memberApi.getSelfRegistrationPasswordLink(this.academy_id, this.createdMemberId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (res?.response === SUCCESS && typeof res?.invite === 'string' && res.invite) {
            this.passwordSetupLink = res.invite;
            this.passwordLinkLoading = false;
            // Redirect immediately in-browser instead of making the parent check email;
            // the link is an absolute, possibly academy-specific URL landing on a standalone
            // routed page, so this must be a full navigation, not an Angular router route.
            window.location.href = res.invite;
            return;
          }
          // 202 "pending": payment not confirmed on the backend yet.
          if (res?.response === 'pending') { retryOrFail(); return; }
          fail();
        },
        // 404/409 (not eligible / already set up) and network errors are terminal.
        error: () => fail()
      });
  }

  // ---- shared form logic (copied from self-registration) ------------------
  onPlanChange(event: Event) {
    const selectedValue = (event.target as HTMLSelectElement).value;
    this.addPersonalInfo.get('plan').setValue(selectedValue === 'remove' ? '' : selectedValue);
  }

  checkAchievementValidity() {
    const controls = this.addAchievement.controls;
    return controls.event.value && controls.achievement.value && controls.year.value && this.addAchievement.valid;
  }

  saveAchievement() {
    if (this.addAchievement.valid) {
      this.achievementList.push({
        event_name: this.addAchievement.get('event').value.trim(),
        achievement: this.addAchievement.get('achievement').value.trim(),
        date: this.addAchievement.get('year').value.toString()
      });
      this.addAchievement.reset();
    }
  }

  removeAchievement(index: number) { this.achievementList.splice(index, 1); }

  checkCareerValidity() {
    const controls = this.addCareer.controls;
    return controls.academy.value && controls.role.value && controls.start_date.value && controls.end_date.value && this.addCareer.valid;
  }

  createCareer() {
    if (this.addCareer.valid) {
      this.careerList.push({
        academy_name: this.addCareer.get('academy').value.trim(),
        role: this.addCareer.get('role').value.trim(),
        start_date: this.addCareer.get('start_date').value,
        end_date: this.addCareer.get('end_date').value
      });
      this.addCareer.reset();
    }
  }

  removeCareer(index: number) { this.careerList.splice(index, 1); }

  checkFormValidity() {
    const controls = this.addEducation.controls;
    return controls.institute.value && controls.discipline.value && controls.start_Year.value && controls.end_Year.value && this.addEducation.valid;
  }

  createEducation() {
    if (this.addEducation.valid) {
      this.educationList.push({
        institute: this.addEducation.get('institute').value.trim(),
        discipline: this.addEducation.get('discipline').value.trim(),
        start_date: this.addEducation.get('start_Year').value.toString(),
        end_date: this.addEducation.get('end_Year').value.toString()
      });
      this.addEducation.reset();
    }
  }

  removeEducation(index: number) { this.educationList.splice(index, 1); }

  calculateBMI() {
    const height = this.addPhysicalChara.get('height').value;
    const weight = this.addPhysicalChara.get('weight').value;
    if (height && weight && !isNaN(height) && !isNaN(weight)) {
      const heightInMeters = height / 100;
      const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(2);
      this.addPhysicalChara.get('bmi').setValue(bmi);
    } else {
      this.addPhysicalChara.get('bmi').setValue('');
    }
  }

  updateParentalValidators(isUnder13: boolean) {
    if (isUnder13) {
      // Under 13: parent name is required, and at least ONE of parent email / WhatsApp number
      // must be provided (group-level rule). Neither contact field is individually required.
      this.addPersonalInfo.controls['pemail'].setValidators([Validators.email]);
      this.addPersonalInfo.controls['pfname'].setValidators([Validators.required, noWhitespaceValidator()]);
      this.addPersonalInfo.controls['pphonenumber'].setValidators([Validators.pattern(PHONE_PATTERN)]);
      this.addPersonalInfo.setValidators(requireAtLeastOne(['pemail', 'pphonenumber'], 'parentContactRequired'));
    } else {
      // 13+ players must supply at least one of: their own email, their own phone,
      // parent/guardian email, or parent/guardian phone.
      this.addPersonalInfo.controls['pemail'].setValidators([Validators.email]);
      this.addPersonalInfo.controls['pfname'].clearValidators();
      this.addPersonalInfo.controls['pphonenumber'].setValidators([Validators.pattern(PHONE_PATTERN)]);
      this.addPersonalInfo.setValidators(requireAtLeastOne(['email', 'phonenumber', 'pemail', 'pphonenumber'], 'playerContactRequired'));
    }
    this.addPersonalInfo.controls['pemail'].updateValueAndValidity();
    this.addPersonalInfo.controls['pfname'].updateValueAndValidity();
    this.addPersonalInfo.controls['pphonenumber'].updateValueAndValidity();
    this.addPersonalInfo.controls['email'].updateValueAndValidity();
    this.addPersonalInfo.updateValueAndValidity();
  }

  usernamePetWords = [
    'mink', 'crab', 'otter', 'falcon', 'panther', 'sparrow', 'badger', 'heron', 'lynx', 'weasel',
    'raven', 'beetle', 'gecko', 'wombat', 'puffin', 'cobra', 'ferret', 'jackal', 'mongoose', 'tapir',
  ];

  generateUsername() {
    if (this.addPersonalInfo.controls['lname'].value.length > 0)
      this.username = `${this.addPersonalInfo.controls['fname'].value}.${this.addPersonalInfo.controls['lname'].value}`;
    else
      this.username = `${this.addPersonalInfo.controls['fname'].value}`;
    this.username = this.username.replace(/\s+/g, '-').toLowerCase();
    this.addPersonalInfo.patchValue({ username: this.username });
    if (this.username) this.resolveUniqueUsername(this.username, 0);
  }

  // resolveUniqueUsername actually verifies the candidate is free, escalating through a
  // digit suffix and then a pet-name suffix on collision, instead of assuming a single
  // random suffix is enough.
  resolveUniqueUsername(candidate: string, tier: number) {
    this.auth.checkUsername(candidate).subscribe((res: any) => {
      if (!res.success) {
        this.username = candidate;
        this.usernameValid = false;
        this.addPersonalInfo.patchValue({ username: this.username });
      } else if (tier === 0) {
        this.resolveUniqueUsername(`${candidate}${this.makeRandom(4)}`, 1);
      } else if (tier < 6) {
        const word = this.usernamePetWords[Math.floor(Math.random() * this.usernamePetWords.length)];
        this.resolveUniqueUsername(`${candidate}.${word}`, tier + 1);
      } else {
        // Safety net so this can never loop forever.
        this.username = `${candidate}${Date.now()}`;
        this.usernameValid = false;
        this.addPersonalInfo.patchValue({ username: this.username });
      }
    });
  }

  checkUserName(generate = false) {
    this.usernameValid = true;
    this.auth.checkUsername(this.username).subscribe((res: any) => {
      if (res.success) {
        if (generate) {
          this.username = `${this.username}${this.makeRandom(4)}`;
          this.addPersonalInfo.patchValue({ username: this.username });
        } else {
          this.usernameValid = true;
        }
      } else {
        this.usernameValid = false;
      }
    });
  }

  makeRandom(lengthOfCode: number) {
    const possible = '0123456789';
    let text = '';
    for (let i = 0; i < lengthOfCode; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  onPhoneNumberInput(event: Event) {
    const inputValue: string = (event.target as HTMLInputElement).value;
    const cleanedValue: string = inputValue.replace(/[^0-9+ ]/g, '');
    this.addPersonalInfo.patchValue({ pphonenumber: cleanedValue });
  }

  onPhoneNumberInputPlayer(event: Event) {
    const inputValue: string = (event.target as HTMLInputElement).value;
    const cleanedValue: string = inputValue.replace(/[^0-9+ ]/g, '');
    this.addPersonalInfo.patchValue({ phonenumber: cleanedValue });
  }

  loadProducts() {
    if (!this.id) return;
    this.memberApi.getProducts(this.id).subscribe({
      next: (res: any) => {
        if (res.status === SUCCESS) {
          this.products = res.data;
          this.memberships = [];
          for (let index = 0; index < this.products?.length; index++) {
            const element = this.products[index];
            if (element.is_active) {
              this.memberships.push({ name: element.name, _id: element._id, ...element });
            }
            this.lookup.set(element._id, element.name);
          }
        }
      },
      error: (err: any) => { console.log(err); this.products = null; }
    });
  }

  getSportsList() {
    this.api.getSportList().subscribe((res: any) => { this.sportsList = res; });
  }

  formDisableClear() {
    this.addPersonalInfo.get('fname')?.enable();
    this.addPersonalInfo.get('lname')?.enable();
    this.addPersonalInfo.get('gender')?.enable();
    this.addPersonalInfo.get('dob')?.enable();
    this.addPersonalInfo.get('username')?.enable();
  }

  checkUser() {
    this.existingUser = false;
    this.formDisableClear();
    this.isValid = true;
    this.email = this.addPersonalInfo.controls['email'].value.trim().toLowerCase();
    this.already_a_academy_member = false;
    if (!this.addPersonalInfo.controls['email'].errors?.email && this.email != '') {
      this.auth.checkEmail(this.email.trim().toLowerCase()).subscribe((result: any) => {
        if (result.active_status === STATUS_ACTIVE || result.active_status === 'new'
          || result.active_status === STATUS_PENDING_CONSENT || result.active_status === STATUS_PENDING_PASSWORD) {
          this.auth.fetchUserId(this.email.trim().toLowerCase()).subscribe({
            next: (res: any) => {
              this.memberApi.isMember(this.academy_id, res.id).subscribe((members: any) => {
                this.already_a_academy_member = members['members'];
                this.auth.fetchUserData(res.id).subscribe(user => {
                  if (user.data.profile_type.includes('Academy')) {
                    this.toastr.warning('This email belongs to an academy. An academy cannot be a member of another academy.', 'Email belongs to an academy', { progressBar: true, closeButton: true });
                    this.isValid = false;
                  } else if (result.profile_type?.includes('Parent')) {
                    this.toastr.warning('This member is already part of Dartle as a parent, a parent cannot be a member of the academy.', 'Member Already Registered as Parent');
                    this.isValid = false;
                  } else if (this.already_a_academy_member) {
                    this.toastr.warning('This email belongs to an existing academy member.', 'Email belongs to a member', { progressBar: true, closeButton: true });
                    this.isValid = false;
                  } else {
                    this.existingUser = true;
                    this.populateForm(user.data);
                    this.checkAge();
                    this.showInfoToast();
                  }
                });
              });
            }
          });
        } else {
          this.isValid = true;
        }
      }, (error: any) => { console.log(error); });
    }
  }

  populateForm(user: UserSuperData) {
    this.isValid = false;
    this.existingUserID = user._id.toString();
    if (user.basic_information.dob.toString() === '0001-01-01T00:00:00Z') {
      this.date = new Date();
    } else {
      this.date = new Date(user.basic_information.dob.toString());
    }
    this.addPersonalInfo.patchValue({
      fname: user.basic_information.name.first_name,
      lname: user.basic_information.name.last_name,
      gender: user.basic_information.gender,
      dob: this.date,
      username: user.user_name
    });
    this.usernameValid = false;
    this.isValid = true;
  }

  checkParentUser() {
    this.parentExistingUser = false;
    this.parentValid = true;
    this.parent_already_a_academy_member = false;
    this.parent_is_academy = false;
    const pemail = (this.addPersonalInfo.controls['pemail'].value || '').trim().toLowerCase();
    if (!this.addPersonalInfo.controls['pemail'].errors?.['email'] && pemail != '') {
      this.auth.checkEmail(pemail).subscribe((result: any) => {
        if (result.active_status === STATUS_ACTIVE || result.active_status === 'new'
          || result.active_status === STATUS_PENDING_CONSENT || result.active_status === STATUS_PENDING_PASSWORD) {
          this.auth.fetchUserId(pemail).subscribe((res: any) => {
            this.memberApi.isMember(this.academy_id, res.id).subscribe((members: any) => {
              this.parent_already_a_academy_member = members['members'];
              this.auth.fetchUserData(res.id).subscribe((user: any) => {
                if (user.data.profile_type.includes('Academy')) {
                  this.parent_is_academy = true;
                  this.parentValid = false;
                  this.toastr.warning('This email belongs to an academy and cannot be added as a parent.', 'Email belongs to an academy', { progressBar: true, closeButton: true });
                } else if (this.parent_already_a_academy_member) {
                  this.parentValid = false;
                  this.toastr.warning('This email is already used by an academy member.', 'Email belongs to a member', { progressBar: true, closeButton: true });
                } else {
                  this.parentExistingUser = true;
                  this.populateParentForm(user.data);
                  this.toastr.info('Existing Dartle user found — parent details auto-filled.', 'Info', { progressBar: true, closeButton: true });
                }
              });
            });
          });
        }
      }, (error: any) => { console.log(error); });
    }
  }

  populateParentForm(user: any) {
    this.parentExistingUserID = user?._id?.toString();
    const phones = user?.basic_information?.phone;
    const parentPhone = Array.isArray(phones) ? (phones.find((p: any) => p?.is_primary) || phones[0]) : null;
    if (parentPhone?.country_code) {
      this.phoneCodeControl.setValue(parentPhone.country_code);
    }
    this.addPersonalInfo.patchValue({
      pfname: user?.basic_information?.name?.first_name || '',
      plname: user?.basic_information?.name?.last_name || '',
      pphonenumber: parentPhone?.number || '',
    });
  }

  public ageFromDateOfBirthday(dateOfBirth: any): number {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  }

  checkAge() {
    this.date = this.addPersonalInfo.controls['dob'].value;
    const age = this.ageFromDateOfBirthday(this.date);
    this.isUnder13 = age <= 13;
    if (age < 3) {
      this.date.setFullYear(this.date.getFullYear() - 9);
      this.toastr.warning('Member must be at least 3 years old.', 'Underage', { progressBar: true, closeButton: true });
      return false;
    }
    // Email is always optional at the field level; required-ness for 13+ is enforced at group level.
    this.addPersonalInfo.controls['email'].setValidators([Validators.email]);
    this.addPersonalInfo.controls['email'].updateValueAndValidity();
    this.updateParentalValidators(this.isUnder13);
    return true;
  }

  showInfoToast() {
    this.toastr.info('This email belongs to an existing Dartle member. We have fetched their data for you.', 'Info', { progressBar: true, closeButton: true });
    this.addPersonalInfo.get('fname')?.disable();
    this.addPersonalInfo.get('lname')?.disable();
    this.addPersonalInfo.get('gender')?.disable();
    this.addPersonalInfo.get('dob')?.disable();
    this.addPersonalInfo.get('username')?.disable();
  }

  onImgError(event: any) { event.target.src = 'images/samples/ic_profile.png'; }

  showAdditional() { this.show_additional_info = !this.show_additional_info; }

  scrollToCard(cardId: string): void {
    const map: any = {
      card1: this.card1, card2: this.card2, card3: this.card3, card4: this.card4,
      card5: this.card5, card6: this.card6, card7: this.card7
    };
    const target = (map[cardId] || this.card1)?.nativeElement;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  getCountryList() {
    this.api.getCountryList().subscribe((res: any) => { this.countryList = [...res]; this.countryListAll = res; });
  }

  search() {
    this.countryList = this.countryListAll.filter((element: any) => element.country.toLowerCase().includes(this.searchTerm.toLowerCase()));
  }

  searchState() {
    this.stateList = this.stateListAll.filter((element: any) => element.toLowerCase().includes(this.searchTermState.toLowerCase()));
  }

  searchPostcode() {
    this.pincodeList = this.pincodeListAll.filter((element: any) => element.toLowerCase().includes(this.searchTermPincode.toLowerCase()));
  }

  selectCountry(country: any) {
    this.countryName = country.country;
    this.countryId = country._id;
    this.countryIso = country.iso;
    this.memberApi.getStateList(this.countryIso).subscribe((res: any) => {
      this.stateList = res['result'];
      this.stateListAll = res['result'];
      this.stateName = 'Select State';
      this.pincodeName = 'Select Pincode';
    });
  }

  selectState(country: any) {
    this.stateName = country;
    this.state = this.stateName;
    this.memberApi.getPincodes(this.stateName, this.countryIso).subscribe((res: any) => {
      this.pincodeList = res['result'];
      this.pincodeListAll = res['result'];
      this.pincodeName = 'Select Pincode';
    });
  }

  selectPostcode(elem: any) {
    this.pincodeName = elem;
    this.postcode = this.pincodeName;
  }

  openMediumModal(mediumModalContent: any) {
    this.modalReference = this.modalService.open(mediumModalContent, { windowClass: 'myCustomModalClass' });
    this.modalReference.result.then(() => { /* */ }, () => {
      this.afterImageUpload = false;
      this.fileSelect = true;
      this.fileSelectDone = false;
      this.file = null;
    });
  }

  fileChangeEvent(event: any): void {
    const fileName = (document.getElementById('myfileid') as HTMLInputElement).value;
    const idxDot = fileName.lastIndexOf('.') + 1;
    const extFile = fileName.substr(idxDot, fileName.length).toLowerCase();
    this.file = event.target.files;
    if (extFile === 'jpg' || extFile === 'jpeg' || extFile === 'png' || extFile === 'pdf') {
      this.fileSelect = false;
      this.imageChangedEvent = event;
      this.afterImageUpload = extFile === 'jpg' || extFile === 'jpeg' || extFile === 'png';
      this.fileSelectDone = extFile === 'pdf';
    } else {
      this.afterImageUpload = false;
      this.fileSelectDone = false;
      this.toastr.warning('Please select a proper file type (JPG, PNG, PDF).', 'File type invalid', { progressBar: true, closeButton: true });
    }
  }

  openFileSelect() { $('#myfileid').trigger('click'); return true; }

  openAttachment(url: any) { window.open(url, '_blank'); }

  imageCropped(event: ImageCroppedEvent) { this.croppedImage = event.base64; }
  imageLoaded() { /* */ }
  cropperReady() { /* */ }
  loadImageFailed() { /* */ }

  async uploadAttachment() {
    if (this.file) {
      const fileName = this.file[0].name;
      if (/[\\/\\:?*"<>|#]/.test(fileName)) {
        this.toastr.warning('File name contains special characters (/\\:?*"<>|#). Please change the file name before uploading.', 'Invalid file name', { progressBar: true, closeButton: true });
        return;
      }
      this.afterCrop = true;
      const uploadPath = `${this.id}/${this.generatedId}/licence`;
      try {
        const endPath = await this.uploadApi.azureupload(this.file[0], storage_user, container_user, uploadPath);
        this.licenseList.push({ name: endPath.upload_file, path: endPath.uploadpath, type: endPath.upload_file_type });
        this.toastr.success('File uploaded successfully.', 'Upload Successful', { progressBar: true, closeButton: true });
        this.modalService.dismissAll();
      } catch (error) {
        console.error('Upload failed:', error);
        this.toastr.error('File upload failed. Please try again.', 'Upload Failed', { progressBar: true, closeButton: true });
      }
    }
  }

  async removeAttachment(path: string) {
    try {
      const filepath = path.split(`windows.net/${container_user}/`)[1];
      await this.uploadApi.azuredelete(storage_user, container_user, filepath);
      this.licenseList = this.licenseList.filter(license => license.path !== path);
      this.toastr.success('Attachment deleted successfully.', 'Deletion Successful', { progressBar: true, closeButton: true });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      if (error instanceof Error && error.message.includes('The specified blob does not exist')) {
        this.toastr.warning('The specified attachment does not exist.', 'Attachment Not Found');
      } else {
        this.toastr.error('An error occurred while deleting the attachment.', 'Error');
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
